import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Image as AntImage, Spin } from 'antd';
import {
  getLessonQuestionById,
  updateQuestionAnswer,
} from '@/services/questions';
import { generateChoices } from '@/utils/helpers/questions';
import Choice from '@/components/Choice';
import E12Spinner from '@/components/E12Spinner';
import ErrorView from '@/components/Error';
import QsPlayer from '@/components/QsPlayer';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

export const QuestionDetail = () => {
  const { questionId } = useParams();

  console.log('questionId', questionId);
  const [selectedChoice, setSelectedChoice] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState<boolean>(false);
  const [feedbackMessage, setFeedbackMessage] = useState<React.ReactNode>('');

  const { t } = useTranslation('translation', {
    keyPrefix: 'components.question-detail',
  });

  const {
    data: questionData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['questions', questionId],
    queryFn: () => getLessonQuestionById(questionId as string),
  });

  useEffect(() => {
    if (questionData?.studentAnswer) {
      handleSelect(questionData.studentAnswer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionData]);

  if (isLoading) {
    return <E12Spinner className="mt-40" />;
  }

  if (error || !questionData) {
    return <ErrorView />;
  }

  const handleSelect = (choice: string) => {
    if (!questionData.studentAnswer) {
      updateQuestionAnswer(questionId as string, choice); // no need to block the flow with awaiting here
    }

    setSelectedChoice(choice);

    const isCorrect = generateChoices(
      questionData.multipleChoiceAnswer.choiceCount,
      questionData.multipleChoiceAnswer.answer
    ).find((option) => option.choice === choice)?.isCorrect;

    if (isCorrect) {
      setFeedbackMessage(
        <p className="dark:text-gray-200">
          {t('correct-answer')} <br />
          <span className="text-gray-500 dark:text-gray-200 text-sm">
            {t('correct-answer-note')}
          </span>
        </p>
      );
    } else {
      setFeedbackMessage(
        <p className="dark:text-gray-200">
          {t('wrong-answer')} <br />
          <span className="text-gray-500 dark:text-gray-200 text-sm">
            {t('wrong-answer-note')}
          </span>
        </p>
      );
    }

    setShowFeedback(true);
  };

  if (!questionId) {
    return <ErrorView />;
  }

  return (
    <div className="sm:px-4 lg:px-32 md:grid md:grid-cols-2 gap-4">
      <div className="flex justify-center dark:bg-white">
        {/* <img
          src={`${questionData.questionMeta.host}${questionData.questionMeta.jpg_url}${questionData.questionMeta.jpg_file}`}
          alt="question_detail_img"
          className="w-full h-full object-contain max-h-96"
        /> */}
        <AntImage
          width={questionData.questionMeta.width * 2}
          height={questionData.questionMeta.height * 2}
          alt="question_detail_img"
          src={`${questionData.questionMeta.host}${questionData.questionMeta.jpg_url}${questionData.questionMeta.jpg_file}`}
          preview={{
            className: 'bg-white',
            mask: <p>{t('zoom-image')}</p>,
          }}
          placeholder={
            <div className="flex justify-center items-center h-full bg-gray-100">
              <Spin />
            </div>
          }
        />
      </div>
      <div className="w-full">
        <h2 className="text-2xl text-center mb-8 text-gray-600 dark:text-gray-200">
          {t('choices')}
        </h2>
        <div className="flex gap-x-4" data-testid={'answers-choices'}>
          {generateChoices(
            questionData.multipleChoiceAnswer.choiceCount,
            questionData.multipleChoiceAnswer.answer
          ).map((option) => (
            <Choice
              key={option.choice}
              choice={option.choice}
              isCorrect={option.isCorrect}
              onSelect={handleSelect}
              isSelected={selectedChoice === option.choice}
              disabled={!!selectedChoice}
              showCorrectAnswer={!!selectedChoice}
            />
          ))}
        </div>
        <AnimatePresence>
          {showFeedback && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5 }}
              className="mt-8 p-4 text-center rounded-lg border shadow-md cursor-pointer"
              data-testid={'questions-feedback'}
            >
              <div className="flex flex-col items-center">
                <motion.p
                  className="text-lg font-semibold text-gray-700"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1, ease: 'easeInOut' }}
                >
                  {feedbackMessage}
                </motion.p>
                <QsPlayer questionMeta={questionData.questionMeta} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
